import * as React from 'react';
import { CSSProperties } from 'react';
import { StyledChip, StyledDivForRoundIndicator, StyledStatusChip, StyledStatusChipProgress } from './utils';
export type ChipStatus = 'success' | 'default' | 'warning' | 'error' | 'unknown' | 'disabled';
export type ChipSize = 'tiny' | 'small' | 'medium' | 'large';
export type ChipVariant = 'default' | 'status' | 'social' | 'progress';

interface DefaultVariantProps {
  variant?: 'default' | 'status' | 'social';
  children: string;
  processingTime?: never;
  progressPerc?: never; // non applicabile per default, status, e social
}

interface ProgressVariantProps {
  variant: 'progress';
  processingTime?: number;
  progressPerc?: number;
  children?: never; // non applicabile per progress
}

export type ChipProps = (DefaultVariantProps | ProgressVariantProps) & {
  status?: ChipStatus;
  size?: ChipSize;
  style?: CSSProperties;
};

const Chip: React.FC<ChipProps> = ({ status = 'default', variant = 'default', size = 'medium', style = {}, children, processingTime, progressPerc }) => {
  const [progress, setProgress] = React.useState<number>(progressPerc || 0); // Stato del progresso iniziale
  const [progressText, setProgressText] = React.useState<string>('Processing');
  const [backgroundColor, setBackgroundColor] = React.useState<string>('#5359EF'); // Colore iniziale

  // Effetto per processingTime
  React.useEffect(() => {
    if (processingTime && !progressPerc) {
      const interval = 100; // aggiornamento ogni 100ms
      const step = (100 / (processingTime * 1000)) * interval;

      const intervalId = setInterval(() => {
        setProgress((prev) => {
          const nextProgress = prev + step;
          if (nextProgress >= 100) {
            clearInterval(intervalId);
            setProgressText('Completed');
            setBackgroundColor('#10B981');
            return 100;
          }
          return nextProgress;
        });
      }, interval);

      return () => clearInterval(intervalId);
    }
    if (progressPerc) {
      // Se progressPerc è fornito, aggiorna il progresso
      setProgress(progressPerc);
      if (progressPerc >= 100) {
        setProgressText('Completed');
        setBackgroundColor('#10B981');
        return;
      }
      setProgressText('Processing');
      setBackgroundColor('#5359EF');
    }
  }, [processingTime, progressPerc]);

  const renderProgressText = () => {
    const textArray = progressText.split('');
    return textArray.map((char, index) => {
      const charProgress = ((index + 1) / textArray.length) * 88;
      const charColor = progress >= charProgress ? 'white' : '#5359EF';
      return (
        <span
          key={index}
          style={{
            color: charColor,
            transition: 'color 0.1s linear',
          }}
        >
          {char}
        </span>
      );
    });
  };

  if (variant === 'progress') {
    return (
      <StyledStatusChipProgress
        status={status}
        size={size}
        style={style}
        progress={progress}
        backgroundColor={backgroundColor} // Passa il colore dello sfondo
        data-testid={`status-${status}`}
      >
        {renderProgressText()}
      </StyledStatusChipProgress>
    );
  }

  if (variant === 'default' || variant === undefined) {
    return (
      <StyledChip
        status={status}
        size={size}
        lenght={children?.length || 0}
        style={{
          ...style,
        }}
        data-testid={`status-${status}`}
      >
        {children}
      </StyledChip>
    );
  }

  return (
    <StyledStatusChip status={status} size={size} style={{ ...style }} data-testid={`status-${status}`}>
      <StyledDivForRoundIndicator status={status} />
      {children}
    </StyledStatusChip>
  );
};

export default Chip;
